import { useTransitionNavigate } from '../../lib/viewTransition'
import { fbt } from 'fbtee'
import { PregameGoerIcon } from '../../components/PregameStrip'
import { EmptyState } from '../../components/EmptyState'
import { InterestBadge } from '../../components/InterestBadge'
import { UserRow } from '../../components/UserRow'
import { Section } from '../../components/Section'
import { unknownMemberName } from '../../lib/commonStrings'
import { placeholders } from '../../lib/skeletonUtils'
import { TicketHolderIcon } from '../../components/TicketHolderIcon'
import { CrewGuestUpsell } from './CrewGuestUpsell'
import { useGuestGuard } from '../../hooks/useGuestGuard'
import { crewPrompt } from '../../lib/guestPrompts'
import type { Group, UserProfile } from '../../types'

const NO_HOLDERS: ReadonlySet<string> = new Set()
const NO_PENDING: ReadonlyMap<string, string> = new Map()

// Builds the "Friend · Group A · Group B" provenance line for one person.
function sourceLabel(
  uid: string,
  isFriend: boolean,
  linkedGroups: Group[],
): string {
  const parts: string[] = []
  if (isFriend) {
    parts.push(fbt('Friend', 'Source label marking a person as the current user’s friend'))
  }
  for (const group of linkedGroups) {
    if (group.memberIds.includes(uid)) parts.push(group.name)
  }
  return parts.join(', ')
}

export function PeopleSection({
  loading,
  linkedGroups,
  friendIds,
  goingUserIds,
  interestedUserIds,
  userProfiles,
  profilesLoading,
  currentUserId,
  pregameGoers,
  ticketHolderIds,
  ticketPendingFrom,
}: {
  /** True while friends' and co-members' statuses may still arrive — the list can still grow. */
  loading?: boolean
  linkedGroups: Group[]
  friendIds: string[]
  goingUserIds: Set<string>
  interestedUserIds: Set<string>
  userProfiles: Record<string, UserProfile>
  /** True while some profile may still arrive — a missing one is a skeleton, not "Unknown". */
  profilesLoading?: boolean
  currentUserId: string | null
  /** People going to a pre-game the viewer can see; their rows get a 🍸. */
  pregameGoers?: ReadonlySet<string>
  /** Crew members holding a ticket — listed as Going (with a ticket mark) even without an RSVP. */
  ticketHolderIds?: ReadonlySet<string>
  /** Crew member → who bought them a ticket they haven't confirmed — listed as tentative, after holders. */
  ticketPendingFrom?: ReadonlyMap<string, string>
}) {
  const navigate = useTransitionNavigate()
  const { isGuest, prompt } = useGuestGuard()
  const friendSet = new Set(friendIds)

  // goingUserIds ∪ interestedUserIds is already the deduplicated set of everyone
  // relevant (linked-group members ∪ friends ∪ self), so iterating it merges the
  // old per-group + friends lists into one row per person.
  const holders = ticketHolderIds ?? NO_HOLDERS
  const pendings = ticketPendingFrom ?? NO_PENDING
  // A ticket in hand means going, RSVP or not.
  const going = (uid: string) => goingUserIds.has(uid) || holders.has(uid)
  const rank = (uid: string) => (going(uid) ? 0 : pendings.has(uid) ? 1 : 2)
  const relevant = [...new Set([...goingUserIds, ...interestedUserIds, ...holders, ...pendings.keys()])].sort((a, b) => {
    if (a === currentUserId) return -1
    if (b === currentUserId) return 1
    if (rank(a) !== rank(b)) return rank(a) - rank(b)
    const an = userProfiles[a]?.displayName ?? ''
    const bn = userProfiles[b]?.displayName ?? ''
    return an.localeCompare(bn)
  })

  return (
    <Section
      title={<>
        {fbt('Going & interested', 'Section heading for people attending in the social tab of the event page')}
        {!loading && relevant.length > 0 && ` (${relevant.length})`}
      </>}
    >
      {isGuest && <CrewGuestUpsell onClick={() => prompt(crewPrompt())} />}
      {isGuest && relevant.length === 0 ? null : relevant.length === 0 && !loading ? (
        <EmptyState>
          {fbt(
            'No one in your groups or friends has marked this event yet',
            'Empty state shown when nobody connected to the user is going or interested in this event',
          )}
        </EmptyState>
      ) : (
        <ul className="flex flex-col gap-3">
          {relevant.map(uid => {
            const profile = userProfiles[uid]
            if (!profile && profilesLoading) return <UserRow key={uid} loading size="lg" />
            const name = profile?.displayName?.trim() || unknownMemberName()
            const isGoing = going(uid)
            const isYou = uid === currentUserId
            const buyer = pendings.get(uid)
            const subtitle = [
              sourceLabel(uid, friendSet.has(uid), linkedGroups),
              buyer && fbt(
                'Ticket from ' + fbt.param('name', userProfiles[buyer]?.displayName?.trim() || unknownMemberName()),
                'People list subtitle: who bought this person a ticket they haven’t confirmed yet',
              ),
            ].filter(Boolean).join(' · ')
            return (
              <UserRow
                key={uid}
                name={name}
                profile={profile}
                size="lg"
                isYou={isYou}
                subtitle={subtitle || undefined}
                action={
                  <span className="flex items-center gap-1.5">
                    {pregameGoers?.has(uid) && <PregameGoerIcon />}
                    {holders.has(uid) && <TicketHolderIcon />}
                    {pendings.has(uid) && <TicketHolderIcon pending />}
                    {isGoing
                      ? <InterestBadge status="going" />
                      : interestedUserIds.has(uid) && <InterestBadge status="interested" />}
                  </span>
                }
                onClick={isYou ? undefined : () => navigate(`/users/${uid}`)}
              />
            )
          })}
          {loading && placeholders(2).map((_, i) => <UserRow key={`placeholder-${i}`} loading size="lg" />)}
        </ul>
      )}
    </Section>
  )
}
